import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Project, Testimonial } from '@/types'

import { getProjects, getTestimonials, pickFeatured } from '@/lib/content'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { ToneCanvas } from '@/components/generals/tone-canvas'
import { DifferentSection } from '@/components/landing/different-section'
import { GratitudeSection } from '@/components/landing/gratitude-section'
import { HeroSection } from '@/components/landing/hero-section'
import { ManifestoSection } from '@/components/landing/manifesto-section'
import { QuoteSection } from '@/components/landing/quote-section'
import { ReadySection } from '@/components/landing/ready-section'
import { ServicesSection } from '@/components/landing/services-section'
import { TestimonialsSection } from '@/components/landing/testimonials-section'
import { WhySection } from '@/components/landing/why-section'
import { WorkSection } from '@/components/landing/work-section'

interface HomeProps {
    featured: Array<Project>
    totalProjects: number
    testimonials: Array<Testimonial>
}

const Home: FC<HomeProps> = ({ featured, totalProjects, testimonials }): ReactNode => {
    return (
        <Layout>
            <Seo />
            <ToneCanvas initial='ink' />

            <HeroSection />
            <ManifestoSection />
            <WhySection />
            <DifferentSection />
            <ServicesSection />
            <WorkSection projects={featured} total={totalProjects} />
            <GratitudeSection />
            <TestimonialsSection testimonials={testimonials} />
            <ReadySection />
            <QuoteSection />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [projects, testimonials] = await Promise.all([getProjects(), getTestimonials()])

    return {
        props: {
            featured: pickFeatured(projects),
            totalProjects: projects.length,
            testimonials
        },
        revalidate: projects.length > 0 ? 60 : 10
    }
}

export default Home
